import { GRAYLOG_DEFINITION_ID } from '@/features/integrations/constants'

import { ROLE_HIERARCHY } from '@/shared/auth/constants'

import type { IntegrationConnector } from '@/features/integrations/types'
import type { Role } from '@/shared/auth/types'

/**
 * Dynamic routes mapped to their parent path (strip UUID/param segment).
 * Order matters: more specific prefixes must come before less specific ones.
 * Keep in sync with src/routes/ dynamic segments ($param routes).
 */
const DYNAMIC_ROUTE_PARENTS: Array<[prefix: string, parent: string]> = [
	['/integrations/new/', '/integrations'],
	['/integrations/cloud/', '/integrations/cloud'],
	['/playbooks/', '/playbooks'],
	['/air/case-management/', '/air/case-management'],
	['/air/automation-settings/', '/air/automation-settings'],
	['/graylog/automation-settings/edit/', '/graylog/automation-settings'],
]

/**
 * Role-gated routes — redirect to /home if target tenant's role is insufficient.
 */
const ROLE_GATED_ROUTES: Record<string, Role> = {
	'/settings': 'Admin',
}

/**
 * Routes gated on an installed integration in the target tenant. If the
 * target tenant lacks the integration, the user is bounced to `/home`.
 *
 * Keep in sync with sidebar visibility (`useCheckIntegrations`) so the
 * post-switch path matches what the user actually sees in the new tenant.
 *
 * Private to this module — both `resolvePostSwitchPath` and
 * `findMissingIntegrationGate` consume it internally so adding a new
 * entry here is a single-line change that propagates everywhere.
 */
const INTEGRATION_GATED_ROUTES: ReadonlyArray<
	readonly [prefix: string, integration: string, definitionId: string]
> = [['/graylog', 'Graylog', GRAYLOG_DEFINITION_ID]] as const

/**
 * Check if a role meets the minimum required role using the hierarchy.
 */
const meetsRole = (userRole: Role | undefined, requiredRole: Role): boolean => {
	if (!userRole) return false
	return (ROLE_HIERARCHY[userRole] ?? 0) >= (ROLE_HIERARCHY[requiredRole] ?? 0)
}

/**
 * Determine whether a given pathname is gated on an integration that the
 * supplied integrations list does NOT include.
 *
 * Single source of truth for the integration-gate check — used by both
 * `resolvePostSwitchPath` (smart-redirect on dropdown switch) and
 * `_dashboard.tsx` beforeLoad (deep-link / pasted-URL gate). Adding a new
 * entry to {@link INTEGRATION_GATED_ROUTES} automatically covers both.
 *
 * @param pathname - the URL pathname to evaluate
 * @param integrations - the target tenant's installed integrations, or
 *   `undefined` to skip the gate (e.g. when integrations aren't in cache —
 *   we'd rather miss a redirect than false-bounce a legitimate user).
 * @returns the blocking gate's integration name when the path is gated and
 *   the integration is missing, otherwise `null`.
 */
export const findMissingIntegrationGate = (
	pathname: string,
	integrations: ReadonlyArray<IntegrationConnector> | undefined,
): string | null => {
	if (integrations === undefined) return null
	for (const [prefix, name, definitionId] of INTEGRATION_GATED_ROUTES) {
		if (!pathname.startsWith(prefix)) continue
		const hasIt = integrations.some(
			(i) => i.connector.definition_id === definitionId,
		)
		return hasIt ? null : name
	}
	return null
}

/**
 * Resolve the best navigation path after a tenant switch.
 *
 * 1. Strip dynamic route segments (UUIDs) → navigate to parent
 * 2. Check role-gated routes against target tenant's role
 * 3. Check integration-gated routes against target tenant's integrations
 * 4. Fallback: stay on current path (static routes pass through)
 *
 * @param pathname - current browser pathname
 * @param targetTenantRole - role the user has in the tenant being switched to
 * @param targetTenantIntegrations - integrations installed in the target
 *   tenant, or `undefined` to skip the integration gate (e.g. when the
 *   target tenant's integrations have not yet been fetched).
 * @returns resolved path to navigate to
 */
export const resolvePostSwitchPath = (
	pathname: string,
	targetTenantRole: Role | undefined,
	targetTenantIntegrations?: ReadonlyArray<IntegrationConnector>,
): string => {
	// Step 1: strip dynamic segments
	let resolvedPath = pathname
	for (const [prefix, parent] of DYNAMIC_ROUTE_PARENTS) {
		if (pathname.startsWith(prefix) && pathname !== prefix) {
			resolvedPath = parent
			break
		}
	}

	// Step 2: check role-gated routes
	for (const [route, requiredRole] of Object.entries(ROLE_GATED_ROUTES)) {
		if (resolvedPath.startsWith(route)) {
			if (!meetsRole(targetTenantRole, requiredRole)) {
				return '/home'
			}
			break
		}
	}

	// Step 3: check integration-gated routes via the shared helper
	if (findMissingIntegrationGate(resolvedPath, targetTenantIntegrations)) {
		return '/home'
	}

	return resolvedPath
}
